import React, { useRef, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDrag, useDrop } from 'react-dnd';
import { useSelector } from 'react-redux';

import { removeDoc, updateDoc } from 'actions/docs';

import { makeGetNotificationsByAssociationId } from 'selectors/notifications';

import Tooltip from 'components/tooltip';
import PromptPopout from 'components/prompt-popout';
import NotificationsBubble from 'components/NotificationsBubble';

import IconNiftyDoc from 'icons/icon-nifty-doc';
import IconRemove from 'icons/icon-remove';
import IconMoveHandle from 'icons/icon-move-handle';

import './DocPagesList.styles.css';

const ITEM_TYPE = 'DOC_PAGE';

const getNotificationsByAssociationId = makeGetNotificationsByAssociationId();

const PageItem = ({ id, index, name, canEdit, selected, moveItem, onClick, onDragStart, onDragEnd, onSelectedRemove }) => {
    const notificationsCount = useSelector(({ notifications }) => getNotificationsByAssociationId(notifications.items, id)).length;
    const { t } = useTranslation(['doc', 'common']);
    const element = useRef(null);
    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(item, monitor) {
            if (!element.current || !canEdit) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) {
                return;
            }
            const hoverBoundingRect = element.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset.y - hoverBoundingRect.top;
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }
            moveItem(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });
    const [{ isDragging }, drag, preview] = useDrag({
        type: ITEM_TYPE,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
        item(monitor) {
            onDragStart && onDragStart(id);
            return { id, index };
        },
        end(item, monitor) {
            onDragEnd && onDragEnd();
        },
        canDrag(monitor) {
            return canEdit;
        }
    });

    const handleCLick = () => {
        onClick(id);
    }

    const handleRemoveClick = useCallback(() => {
        if (selected) {
            onSelectedRemove();
        }

        removeDoc(id);
    }, [id, selected, onSelectedRemove]);

    return (
        <div className={'page-item' + (selected ? ' selected' : '') + (isDragging ? ' dragging' : '')} ref={drop}>
            <div className="page-item-inner" ref={element}>
                <div className="page-item-main" onClick={handleCLick} ref={drag}>
                    <div className="page-item-icon">
                        {
                            canEdit ? <IconMoveHandle /> : null
                        }
                    </div>

                    <span>{ name }</span>

                    <NotificationsBubble count={notificationsCount} />
                </div>

                {
                    canEdit ? (
                        <div className="page-item-actions">
                            <PromptPopout
                                title={t('actions.deletePage.title')}
                                text={t('actions.deletePage.description')}
                                confirmText={ t('common:Remove') }
                                confirmClassName="btn-caution"
                            >
                                <span className="action-item" onClick={handleRemoveClick}>
                                    <Tooltip tooltip={ t('actions.deletePage.title') } withTooltipHover={false}>
                                        <span className="action-item-inner">
                                            <IconRemove />
                                        </span>
                                    </Tooltip>
                                </span>
                            </PromptPopout>
                        </div>
                    ) : null
                }
            </div>
        </div>
    );
}

const DocPagesList = ({ mainPage, pages, canEdit, selectedPage, onPageSelect, onPageCreate }) => {
    const { t } = useTranslation('doc');
    const [draggingPages, setDraggingPages] = useState(null);
    const [draggedPageId, setDraggedPageId] = useState(null);

    const handleDragStart = useCallback(id => {
        setDraggingPages(pages);
        setDraggedPageId(id)
    }, [pages]);

    const handleDragEnd = useCallback(() => {
        const pageIndex = draggingPages.findIndex(({ id }) => id === draggedPageId);
        const prevPageOrder = draggingPages[pageIndex - 1]?.order || 0;
        const nextPageOrder = draggingPages[pageIndex + 1]?.order || ((draggingPages[draggingPages.length - 1]?.order || 0) + 100);

        updateDoc(draggedPageId, {
            order: (prevPageOrder + nextPageOrder) / 2
        });

        setDraggingPages(null);
        setDraggedPageId(null)
    }, [draggingPages, draggedPageId]);

    const handleMove = useCallback((sourceIndex, targetIndex) => {
        const nextDraggingPagesOrder = [...draggingPages];
        const [item] = nextDraggingPagesOrder.splice(sourceIndex, 1);
        nextDraggingPagesOrder.splice(targetIndex, 0, item);

        setDraggingPages(nextDraggingPagesOrder);
    }, [draggingPages]);

    const handleSelectedRemove = useCallback(() => {
        onPageSelect(mainPage.id);
    }, [onPageSelect, mainPage?.id]);

    return (
        <div className="document-pages">
            <PageItem
                key={mainPage.id}
                id={mainPage.id}
                name={mainPage.name}
                selected={selectedPage === mainPage.id}
                onClick={onPageSelect}
            />

            {
                (draggingPages || pages)?.map((page, index) => <PageItem
                    key={page.id}
                    id={page.id}
                    index={index}
                    name={page.name}
                    canEdit={canEdit}
                    selected={selectedPage === page.id}
                    onDragStart={handleDragStart}
                    onDragEnd={handleDragEnd}
                    moveItem={handleMove}
                    onClick={onPageSelect}
                    onSelectedRemove={handleSelectedRemove}
                />)
            }

            {
                canEdit ? (
                    <div className="document-pages-cta">
                        <button className="btn btn-small btn-secondary btn-block" onClick={onPageCreate}>+ { t('addPageCta') }</button>
                    </div>
                ) : null
            }
        </div>
    )

}

export default DocPagesList;
